import React from 'react';
import { Calendar } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DATE_PRESETS, DATE_BASIS, getDateRangeForPreset, validateDateRange } from '../utils/dateFilterUtils';

/**
 * Shared DateFilter component for Dashboard and Reports pages
 *
 * Features:
 * - Date range presets (Today, This Week, This Month, Last 30/90 Days, YTD, Custom)
 * - Date basis selector (Due Date vs Invoice Date)
 * - Custom date picker for start/end dates
 *
 * @param {Object} props
 * @param {string} props.preset - Current preset selection
 * @param {string} props.basis - Current date basis ('due' or 'invoice')
 * @param {string} props.startDate - Start date (YYYY-MM-DD)
 * @param {string} props.endDate - End date (YYYY-MM-DD)
 * @param {Function} props.onChange - Callback when filter changes: ({ preset, basis, startDate, endDate }) => void
 */
const DateFilter = ({ preset, basis, startDate, endDate, onChange }) => {
  const handlePresetChange = (newPreset) => {
    if (newPreset === 'custom') {
      // Keep current dates when switching to custom
      onChange({
        preset: newPreset,
        basis,
        startDate,
        endDate
      });
    } else {
      // Calculate dates for preset
      const { startDate: newStart, endDate: newEnd } = getDateRangeForPreset(newPreset);
      onChange({
        preset: newPreset,
        basis,
        startDate: newStart,
        endDate: newEnd
      });
    }
  };

  const handleBasisChange = (newBasis) => {
    onChange({
      preset,
      basis: newBasis,
      startDate,
      endDate
    });
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    if (validateDateRange(newStartDate, endDate)) {
      onChange({
        preset: 'custom',
        basis,
        startDate: newStartDate,
        endDate
      });
    }
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    if (validateDateRange(startDate, newEndDate)) {
      onChange({
        preset: 'custom',
        basis,
        startDate,
        endDate: newEndDate
      });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Date Range Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Date Range:</span>
        </div>

        <Select value={preset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-full sm:w-[180px] rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(DATE_PRESETS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {preset === 'custom' && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className="rounded-sm border-2 h-10 w-full sm:w-auto"
              placeholder="Start date"
            />
            <Input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              className="rounded-sm border-2 h-10 w-full sm:w-auto"
              placeholder="End date"
            />
          </div>
        )}
      </div>

      {/* Date Basis Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <span className="text-sm font-medium text-slate-700 sm:ml-6">Filter By:</span>
        <Select value={basis} onValueChange={handleBasisChange}>
          <SelectTrigger className="w-full sm:w-[180px] rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(DATE_BASIS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">
          {basis === 'due'
            ? '(Filters invoices by due date)'
            : '(Filters invoices by invoice/issue date)'}
        </span>
      </div>

      {/* Note about aging calculations */}
      <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded-sm border border-slate-200 sm:ml-6">
        <strong>Note:</strong> Aging buckets (0-30, 31-60, etc.) are always calculated based on due date vs today,
        regardless of the selected filter basis, as aging is due-date logic.
      </div>
    </div>
  );
};

export default DateFilter;
